/** Sidebar, top bar, company switcher and the permission-aware menu. */
import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { articleFor } from './help/HelpPage';
import {
  BarChart3,
  BookOpen,
  Building,
  Building2,
  CalendarRange,
  ChevronDown,
  Banknote,
  Boxes,
  CircleHelp,
  Briefcase,
  FlaskConical,
  LifeBuoy,
  Repeat,
  Upload,
  DatabaseBackup,
  Activity,
  Braces,
  Inbox,
  KeyRound,
  Plug,
  Table2,
  Webhook,
  Workflow,
  Download,
  ShoppingCart,
  Stamp,
  Truck,
  FileCheck2,
  HandCoins,
  Package,
  Receipt,
  Users,
  Landmark,
  LayoutDashboard,
  Library,
  ListTree,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Scale,
  ScrollText,
  Settings,
  Sun,
  Tags,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { applyUpdate, useInstallPrompt, useUpdateReady } from '../lib/pwa';
import { useTheme } from '../lib/theme';
import { useSidebarCollapsed } from '../lib/sidebar';
import { Button } from '../components/ui';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  /** Any one of these is enough to see the item. */
  permissions?: string[];
  /** ITCSI platform administrators only. */
  platform?: boolean;
  /** Members of an advisor firm only (M14). */
  firm?: boolean;
  children?: NavItem[];
}

const NAV: NavItem[] = [
  {
    to: '/advisor',
    label: 'Advisor Console',
    Icon: Briefcase,
    firm: true,
    children: [
      { to: '/advisor', label: 'Portfolio', Icon: Briefcase, firm: true },
      { to: '/advisor/team', label: 'Team & Clients', Icon: Users, firm: true },
    ],
  },
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard, permissions: ['dashboard.view'] },
  {
    to: '/sales/invoices',
    label: 'Sales',
    Icon: Receipt,
    permissions: ['sales.view', 'receipts.view', 'customers.view'],
    children: [
      { to: '/sales/invoices', label: 'Invoices', Icon: Receipt, permissions: ['sales.view'] },
      { to: '/sales/quotations', label: 'Quotations & Orders', Icon: Receipt, permissions: ['sales.view'] },
      { to: '/sales/memos', label: 'Credit & Debit Memos', Icon: Receipt, permissions: ['sales.view'] },
      { to: '/receipts', label: 'Receipts', Icon: HandCoins, permissions: ['receipts.view'] },
      { to: '/receipts/2307-tracker', label: '2307 Tracker', Icon: FileCheck2, permissions: ['receipts.view'] },
      { to: '/customers', label: 'Customers', Icon: Users, permissions: ['customers.view'] },
      { to: '/items', label: 'Items & Services', Icon: Package, permissions: ['sales.view', 'bills.view', 'coa.view'] },
    ],
  },
  {
    to: '/purchases/bills',
    label: 'Purchases',
    Icon: ShoppingCart,
    permissions: ['bills.view', 'payments.view', 'suppliers.view'],
    children: [
      { to: '/purchases/bills', label: 'Bills', Icon: ShoppingCart, permissions: ['bills.view'] },
      { to: '/purchases/orders', label: 'Purchase Orders', Icon: ShoppingCart, permissions: ['bills.view'] },
      { to: '/payments', label: 'Payments & Petty Cash', Icon: Banknote, permissions: ['payments.view'] },
      { to: '/suppliers', label: 'Suppliers', Icon: Truck, permissions: ['suppliers.view'] },
    ],
  },
  {
    to: '/inventory',
    label: 'Inventory',
    Icon: Boxes,
    permissions: ['inventory.view'],
    children: [
      { to: '/inventory', label: 'Stock on Hand', Icon: Boxes, permissions: ['inventory.view'] },
      { to: '/inventory/moves', label: 'Stock Movements', Icon: Boxes, permissions: ['inventory.view'] },
      { to: '/inventory/adjustments', label: 'Adjustments & Counts', Icon: Boxes, permissions: ['inventory.view'] },
      { to: '/inventory/locations', label: 'Locations', Icon: Boxes, permissions: ['inventory.view'] },
    ],
  },
  {
    to: '/banking',
    label: 'Banking',
    Icon: Landmark,
    permissions: ['bank.view', 'pdc.view', 'pettycash.manage'],
    children: [
      { to: '/banking', label: 'Accounts & Reconciliation', Icon: Landmark, permissions: ['bank.view'] },
      { to: '/banking/pdc', label: 'Post-dated Checks', Icon: Landmark, permissions: ['pdc.view'] },
      { to: '/banking/petty-cash', label: 'Petty Cash', Icon: Landmark, permissions: ['bank.view', 'pettycash.manage'] },
      { to: '/banking/rules', label: 'Bank Rules', Icon: Landmark, permissions: ['bank.view'] },
    ],
  },
  {
    to: '/tax',
    label: 'BIR Tax',
    Icon: Stamp,
    permissions: ['tax.view', 'wht.issue'],
    children: [
      { to: '/tax', label: 'Tax Calendar & Returns', Icon: Stamp, permissions: ['tax.view'] },
      { to: '/tax/checks', label: 'Pre-filing Checks', Icon: Stamp, permissions: ['tax.view'] },
      { to: '/tax/2307-issued', label: '2307 Issued', Icon: FileCheck2, permissions: ['tax.view', 'wht.issue'] },
      { to: '/receipts/2307-tracker', label: '2307 Received Tracker', Icon: FileCheck2, permissions: ['receipts.view'] },
    ],
  },
  {
    to: '/journals',
    label: 'Accounting',
    Icon: BookOpen,
    permissions: ['journals.view', 'coa.view'],
    children: [
      { to: '/journals', label: 'Journal Entries', Icon: BookOpen, permissions: ['journals.view'] },
      { to: '/opening-balances', label: 'Opening Balances', Icon: Scale, permissions: ['journals.create'] },
      { to: '/accounts', label: 'Chart of Accounts', Icon: ListTree, permissions: ['coa.view'] },
      { to: '/dimensions', label: 'Dimensions', Icon: Tags, permissions: ['coa.view'] },
      { to: '/recurring', label: 'Recurring Journals & Invoices', Icon: Repeat, permissions: ['journals.view', 'sales.view'] },
      { to: '/periods', label: 'Periods & Year-end', Icon: CalendarRange, permissions: ['periods.manage', 'journals.view'] },
    ],
  },
  {
    to: '/reports/statements/sfp',
    label: 'Reports',
    Icon: BarChart3,
    permissions: ['reports.view', 'reports.ar', 'reports.ap', 'reports.cash'],
    children: [
      { to: '/reports/statements/sfp', label: 'Financial Position', Icon: BarChart3, permissions: ['reports.view'] },
      { to: '/reports/statements/sci', label: 'Income Statement', Icon: BarChart3, permissions: ['reports.view'] },
      { to: '/reports/statements/sce', label: 'Changes in Equity', Icon: BarChart3, permissions: ['reports.view'] },
      { to: '/reports/statements/scf', label: 'Cash Flows', Icon: BarChart3, permissions: ['reports.view'] },
      { to: '/reports/trial-balance', label: 'Trial Balance', Icon: BarChart3, permissions: ['reports.view'] },
      { to: '/reports/general-ledger', label: 'General Ledger', Icon: BarChart3, permissions: ['reports.view'] },
      { to: '/reports/aged-receivables', label: 'Aged Receivables', Icon: BarChart3, permissions: ['reports.ar', 'reports.view'] },
      { to: '/reports/customer-statement', label: 'Customer Statement', Icon: BarChart3, permissions: ['reports.ar', 'reports.view'] },
      { to: '/reports/aged-payables', label: 'Aged Payables', Icon: BarChart3, permissions: ['reports.ap', 'reports.view'] },
      { to: '/reports/supplier-statement', label: 'Supplier Statement', Icon: BarChart3, permissions: ['reports.ap', 'reports.view'] },
      { to: '/reports/cash-requirements', label: 'Cash Requirements', Icon: BarChart3, permissions: ['reports.ap', 'reports.view'] },
      { to: '/reports/analysis', label: 'Management Reports', Icon: BarChart3, permissions: ['reports.view', 'reports.ar', 'reports.ap'] },
    ],
  },
  {
    to: '/reports/books/general-journal',
    label: 'Books of Accounts',
    Icon: Library,
    permissions: ['reports.view'],
    children: [
      { to: '/reports/books/general-journal', label: 'General Journal', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/books/general-ledger', label: 'General Ledger', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/books/cash-receipts', label: 'Cash Receipts Book', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/books/cash-disbursements', label: 'Cash Disbursements Book', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/books/sales-journal', label: 'Sales Journal', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/books/purchase-journal', label: 'Purchase Journal', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/books/inventory-book', label: 'Inventory Book', Icon: Library, permissions: ['reports.view'] },
      { to: '/reports/archive', label: 'Archive & BIR Submission', Icon: Library, permissions: ['reports.view', 'audit.view'] },
    ],
  },
  {
    to: '/integrations',
    label: 'Integrations',
    Icon: Plug,
    permissions: ['integrations.manage', 'rules.view', 'events.define', 'exceptions.manage'],
    children: [
      { to: '/integrations', label: 'Monitor', Icon: Activity, permissions: ['integrations.manage', 'rules.view', 'audit.view', 'users.view'] },
      { to: '/integrations/events', label: 'Events & Exceptions', Icon: Inbox, permissions: ['rules.view', 'exceptions.manage'] },
      { to: '/integrations/rules', label: 'Posting Rules', Icon: Workflow, permissions: ['rules.view'] },
      { to: '/integrations/mappings', label: 'Mapping Tables', Icon: Table2, permissions: ['rules.view'] },
      { to: '/integrations/event-types', label: 'Event Types', Icon: Braces, permissions: ['rules.view', 'events.define'] },
      { to: '/integrations/clients', label: 'API Clients', Icon: KeyRound, permissions: ['integrations.manage', 'audit.view'] },
      { to: '/integrations/webhooks', label: 'Webhooks', Icon: Webhook, permissions: ['integrations.manage', 'audit.view'] },
    ],
  },
  {
    to: '/setup/company',
    label: 'Setup',
    Icon: Settings,
    permissions: ['company.view', 'company.manage', 'users.view', 'audit.view', 'coa.manage', 'customers.manage', 'journals.create'],
    children: [
      { to: '/setup/company', label: 'Company & BIR', Icon: Building2, permissions: ['company.view', 'company.manage'] },
      { to: '/setup/branches', label: 'Branches & Invoice Series', Icon: Building2, permissions: ['company.view', 'company.manage'] },
      { to: '/setup/users', label: 'Users', Icon: User, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/advisors', label: 'Advisor Firms', Icon: Briefcase, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/support', label: 'ITCSI Support Access', Icon: LifeBuoy, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/import', label: 'Import Data', Icon: Upload, permissions: ['coa.manage', 'customers.manage', 'suppliers.manage', 'sales.manage', 'bills.manage', 'journals.create'] },
      { to: '/setup/data', label: 'Export & Sandbox', Icon: DatabaseBackup, permissions: ['users.manage', 'company.view'] },
      { to: '/setup/roles', label: 'Roles & Permissions', Icon: Settings, permissions: ['users.view', 'users.manage'] },
      { to: '/setup/audit', label: 'Audit Log', Icon: ScrollText, permissions: ['audit.view'] },
    ],
  },
  { to: '/platform/companies', label: 'Client Companies', Icon: Building, platform: true },
  { to: '/platform/firms', label: 'Advisor Firms', Icon: Briefcase, platform: true },
];

/** Always-visible notices: a sandbox is not the real books; a support seat is watched. */
function Banners(): JSX.Element | null {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      {user.sandbox && (
        <div role="status" className="mb-4 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <FlaskConical aria-hidden="true" className="h-4 w-4 shrink-0" />
          Sandbox — test data only. Documents printed here are watermarked and are not valid for the BIR.
        </div>
      )}
      {user.supportSessionId && (
        <div role="status" className="mb-4 flex items-center gap-2 rounded-xl border border-brand/40 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 dark:bg-slate-800 dark:text-brand-100">
          <LifeBuoy aria-hidden="true" className="h-4 w-4 shrink-0" />
          ITCSI support session: read-only{user.supportEndsAt ? `, ends ${new Date(user.supportEndsAt).toLocaleTimeString('en-PH', { timeZone: 'Asia/Manila', hour: 'numeric', minute: '2-digit' })}` : ''}. Every page you open is logged for the client.
        </div>
      )}
    </>
  );
}

function NavTree({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  /** Icons only. Submenus are hidden: a parent's own link is its landing page. */
  collapsed?: boolean;
}): JSX.Element {
  const { can, user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState<string | null>(
    () => NAV.find((item) => item.children?.some((c) => location.pathname.startsWith(c.to)))?.to ?? null,
  );

  const visible = (item: NavItem): boolean =>
    item.platform ? Boolean(user?.isPlatformAdmin) : item.firm ? Boolean(user?.firm) : !item.permissions || can(...item.permissions);

  return (
    <ul className="space-y-1">
      {NAV.filter(visible).map((item) => {
        const children = collapsed ? [] : (item.children ?? []).filter(visible);
        const expanded = open === item.to;
        return (
          <li key={item.to}>
            <div className="flex items-center">
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                // The label is gone in the rail, so the name has to reach a
                // screen reader and a hovering mouse some other way.
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex flex-1 items-center rounded-xl text-sm font-medium transition ${
                    collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
                  } ${
                    isActive
                      ? 'bg-navy text-white'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`
                }
              >
                <item.Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                {!collapsed && item.label}
              </NavLink>
              {!collapsed && children.length > 0 && (
                <button
                  className="ml-1 grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : item.to)}
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-4 w-4 transition ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
            </div>
            {children.length > 0 && expanded && (
              <ul className="ml-4 mt-1 space-y-1 border-l border-slate-200 pl-3 dark:border-slate-800">
                {children.map((child) => (
                  <li key={child.to}>
                    <NavLink
                      to={child.to}
                      // A parent-level page (e.g. /integrations) must not stay lit on its siblings.
                      end={children.some((other) => other !== child && other.to.startsWith(`${child.to}/`))}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `block rounded-lg px-3 py-1.5 text-sm transition ${
                          isActive
                            ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-brand-100'
                            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`
                      }
                    >
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Switches the session to another company this user belongs to. */
function CompanySwitcher(): JSX.Element | null {
  const { user, companies, switchCompany } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (companies.length < 2) return null;
  return (
    <label className="flex min-w-0 items-center gap-2 text-sm">
      <span className="sr-only">Company</span>
      <select
        className="min-w-0 max-w-[16rem] truncate rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        value={user?.companyId ?? ''}
        disabled={busy}
        onChange={(event) => {
          setBusy(true);
          void switchCompany(event.target.value)
            .then(() => navigate('/', { replace: true }))
            .finally(() => setBusy(false));
        }}
      >
        {companies.map((c) => (
          <option key={c.companyId} value={c.companyId}>
            {c.companyName} — {c.roleName}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { user, signOut } = useAuth();
  const { choice, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const updateReady = useUpdateReady();
  const { canInstall, install } = useInstallPrompt();

  const onSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 pb-safe dark:bg-slate-950">
      <aside
        className={`fixed inset-y-0 left-0 hidden flex-col border-r border-slate-200 bg-white py-5 transition-[width] duration-200 dark:border-slate-800 dark:bg-slate-900 md:flex ${
          collapsed ? 'w-16 px-2' : 'w-64 px-4'
        }`}
      >
        <div className={`mb-6 flex items-center ${collapsed ? 'justify-center' : 'justify-between px-1'}`}>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-lg font-bold text-navy dark:text-white">
                CoreX<span className="text-brand">-lite</span>
              </p>
              <p className="truncate text-xs text-slate-500">{user?.companyName ?? 'No company selected'}</p>
            </div>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand the menu' : 'Collapse the menu'}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" className="h-5 w-5" />
            ) : (
              <PanelLeftClose aria-hidden="true" className="h-5 w-5" />
            )}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto" aria-label="Main">
          <NavTree collapsed={collapsed} />
        </nav>
        <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
          {!collapsed && (
            <>
              <NavLink to="/account" className="block truncate px-3 text-sm font-medium text-slate-700 hover:underline dark:text-slate-200" title="My account and sessions">
                {user?.name}
              </NavLink>
              <p className="truncate px-3 text-xs text-slate-500">{user?.roleName ?? (user?.isPlatformAdmin ? 'Platform admin' : '')}</p>
            </>
          )}
          <button
            onClick={() => void onSignOut()}
            title={collapsed ? `Sign out — ${user?.name ?? ''}` : undefined}
            aria-label={collapsed ? 'Sign out' : undefined}
            className={`mt-2 flex w-full items-center rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 ${
              collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
            }`}
          >
            <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
            {!collapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      <div
        className={`flex min-w-0 flex-1 flex-col transition-[margin] duration-200 ${
          collapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
      >
        {updateReady && (
          <div className="flex flex-wrap items-center justify-between gap-2 bg-brand px-4 py-2 text-sm text-white">
            <span>A newer version of CoreX-lite is ready.</span>
            <button
              type="button"
              onClick={() => void applyUpdate()}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-white/15 px-3 font-semibold hover:bg-white/25"
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" /> Reload now
            </button>
          </div>
        )}

        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 pt-safe backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
          <button
            onClick={() => setMenuOpen((open) => !open)}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 md:hidden"
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </button>
          <span className="font-semibold text-navy dark:text-white md:hidden">CoreX-lite</span>
          <CompanySwitcher />
          <div className="flex-1" />
          {canInstall && (
            <button
              onClick={() => void install()}
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Install CoreX-lite on this device"
              title="Install on this device"
            >
              <Download aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          <Link
            to={`/help/${articleFor(location.pathname)}`}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="Help for this screen"
            title="Help for this screen"
          >
            <CircleHelp aria-hidden="true" className="h-5 w-5" />
          </Link>
          <button
            onClick={toggle}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={`Theme: ${choice}. Click to change.`}
            title={`Theme: ${choice === 'system' ? 'follow system' : choice}`}
          >
            {choice === 'dark' ? (
              <Moon aria-hidden="true" className="h-4 w-4" />
            ) : choice === 'light' ? (
              <Sun aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Monitor aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
          <Button variant="ghost" size="sm" onClick={() => void onSignOut()} className="hidden md:inline-flex">
            <LogOut aria-hidden="true" className="h-4 w-4" /> Sign out
          </Button>
        </header>

        {menuOpen && (
          <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 md:hidden">
            <NavTree onNavigate={() => setMenuOpen(false)} />
          </div>
        )}

        <main className="flex-1 px-4 py-6 md:px-6">
          <Banners />
          {children}
        </main>
      </div>
    </div>
  );
}
